var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Grid, TextField, styled } from '@mui/material';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { PlanType, StatusPermission } from '../../enum';
import { dateToString } from '../../util';
import { useUserPermissionsAPI } from '../app/selectors';
import { Autocomplete, DatePicker, FormField, NumberInput } from '../common';
import { IMPORT_DATA_TYPE, ImportDataType, MAX_PERIOD_LENGTH, PLAN_TYPE } from './constants';
import { useCreatePlanAPI, useUploadDataAPI, useValidatePlanAPI } from './selectors';
import { UploadSuccessStatusDialog } from './uploadStatusDialog';
import { UploadTemplateDialog } from './uploadTemplate';
var StyledForm = styled(Box)(function (_a) {
    var theme = _a.theme;
    return ({
        backgroundColor: theme.palette.primary.light,
        padding: theme.spacing(8),
        borderRadius: theme.shape.borderRadius,
    });
});
export var defaultFormValues = {
    name: '',
    importData: IMPORT_DATA_TYPE[0].id,
    startDate: moment.utc().startOf('d'),
    planType: PlanType.Monthly,
    periodLength: null,
};
var validateForm = yup.object().shape({
    name: yup.string().required().max(128).trim().specialCharacter().quotesNotAllowed().label('Name'),
    importData: yup.number().required(),
    planType: yup.string().required().trim(),
    periodLength: yup
        .number()
        .nullable()
        .when('importData', {
        is: function (value) { return value !== ImportDataType.Excel; },
        then: function (period) { return period.required(); },
    }),
    startDate: yup
        .object()
        .nullable()
        .when('importData', {
        is: function (value) { return value !== ImportDataType.Excel; },
        then: function (date) { return date.required(); },
    }),
    startDateError: yup
        .string()
        .optional()
        .when('importData', {
        is: function (value) { return value !== ImportDataType.Excel; },
        then: function (date) { return date.max(0, 'Invalid'); },
    }),
});
export var CreatePlan = function (_a) {
    var handleImport = _a.handleImport, defaultValues = _a.defaultValues;
    var navigate = useNavigate();
    var t = useTranslation('myPlans').t;
    var userPermission = useUserPermissionsAPI();
    var _b = useState(false), isUpload = _b[0], setIsUpload = _b[1];
    var _c = useState(0), uploadDataId = _c[0], setUploadDataId = _c[1];
    var _d = useState(), uploadedFile = _d[0], setUploadedFile = _d[1];
    var _e = useCreatePlanAPI(), createPlanData = _e[0], createPlan = _e[1], clearCreatePlanData = _e[2];
    var _f = useValidatePlanAPI(), validatePlanData = _f[0], validatePlan = _f[1], clearValidatePlanData = _f[2];
    var _g = useUploadDataAPI(), uploadData = _g[0], fetchUploadData = _g[1], clearUploadData = _g[2];
    var planFormData = useRef();
    var _h = useForm({
        defaultValues: __assign(__assign({}, defaultFormValues), defaultValues),
        resolver: yupResolver(validateForm),
    }), control = _h.control, watch = _h.watch, setValue = _h.setValue, handleSubmit = _h.handleSubmit;
    var _j = watch(), planType = _j.planType, importData = _j.importData;
    var importDataOptions = IMPORT_DATA_TYPE.filter(function (dt) { return !!handleImport || dt.id !== ImportDataType.Forecast; });
    var disableField = importData === ImportDataType.Excel;
    useEffect(function () {
        var _a, _b, _c, _d, _e, _f;
        if (createPlanData.data) {
            navigate("/wizard/".concat((_a = createPlanData.data) === null || _a === void 0 ? void 0 : _a.id));
        }
        if ((_b = uploadData.data) === null || _b === void 0 ? void 0 : _b.invalid) {
            navigate("/wizard/".concat((_c = uploadData.data) === null || _c === void 0 ? void 0 : _c.id));
        }
        else if (!((_d = uploadData.data) === null || _d === void 0 ? void 0 : _d.invalid) && !!((_e = uploadData.data) === null || _e === void 0 ? void 0 : _e.id)) {
            setUploadDataId((_f = uploadData.data) === null || _f === void 0 ? void 0 : _f.id);
        }
        return function () {
            clearUploadData();
            clearCreatePlanData();
        };
    }, [clearCreatePlanData, createPlanData.data, uploadData.data, navigate, clearUploadData]);
    useEffect(function () {
        if (validatePlanData.data && planFormData.current && importData === ImportDataType.Forecast) {
            handleImport === null || handleImport === void 0 ? void 0 : handleImport(planFormData.current);
            clearValidatePlanData();
        }
        return clearValidatePlanData;
    }, [validatePlanData.data, importData, handleImport, clearValidatePlanData]);
    var handleCreatePlan = handleSubmit(function (_a) {
        var startDate = _a.startDate, importValue = _a.importData, values = __rest(_a, ["startDate", "importData"]);
        var payload = __assign(__assign({}, values), { startDate: dateToString(startDate) });
        if (importValue === ImportDataType.Excel) {
            setIsUpload(true);
        }
        else if (importValue === ImportDataType.Forecast) {
            planFormData.current = __assign({ startDate: startDate }, values);
            validatePlan(payload);
        }
        else {
            createPlan(payload);
        }
    });
    var handleUploadComplete = handleSubmit(function (_a) {
        var startDate = _a.startDate, values = __rest(_a, ["startDate"]);
        fetchUploadData(__assign(__assign({}, values), { startDate: dateToString(startDate), file: uploadedFile, importData: undefined }));
        setIsUpload(!isUpload);
    });
    return (_jsxs(_Fragment, { children: [_jsx(StyledForm, __assign({ component: "form", onSubmit: handleCreatePlan }, { children: _jsxs(Grid, __assign({ container: true, alignItems: "flex-start" }, { children: [_jsxs(Grid, __assign({ item: true, xs: 12, lg: 10, xl: 10.5, spacing: 5, container: true }, { children: [_jsx(Grid, __assign({ item: true, xs: "auto", style: { flex: 1 } }, { children: _jsx(FormField, __assign({ required: true, name: "name", label: t('nameOfPlan'), control: control }, { children: _jsx(TextField, { inputProps: {
                                                maxLength: 128,
                                            } }) })) })), _jsx(Grid, __assign({ item: true, xs: 1.6 }, { children: _jsx(FormField, __assign({ required: true, name: "importData", label: t('importData'), control: control }, { children: _jsx(Autocomplete, { options: importDataOptions }) })) })), _jsx(Grid, __assign({ item: true, xs: 1.6 }, { children: _jsx(FormField, __assign({ required: true, name: "startDate", label: t('planStartDate'), control: control }, { children: _jsx(DatePicker, { control: control, disabled: !!disableField }) })) })), _jsx(Grid, __assign({ item: true, xs: 1.6 }, { children: _jsx(FormField, __assign({ required: true, name: "planType", label: t('planType'), control: control }, { children: _jsx(Autocomplete, { options: PLAN_TYPE, getOptionValue: function (o) { return o.value; }, onChange: function () { return setValue('periodLength', null); }, "data-testid": "planTypeSelect", disabled: !!disableField }) })) })), _jsx(Grid, __assign({ item: true, xs: 1.6 }, { children: _jsx(FormField, __assign({ required: true, name: "periodLength", label: t('length'), control: control }, { children: _jsx(NumberInput, { placeholder: t("numberPlaceholder".concat(planType)), min: 1, max: MAX_PERIOD_LENGTH[planType], fullWidth: true, disabled: !!disableField }) })) }))] })), _jsx(Grid, __assign({ item: true, xs: 12, lg: 2, xl: 1.5, display: "inline-flex", justifyContent: "flex-end" }, { children: _jsx(LoadingButton, __assign({ disabled: (userPermission === null || userPermission === void 0 ? void 0 : userPermission.capacityPermission) !== StatusPermission.Edit, variant: "contained", type: "submit", sx: { marginTop: '1.75rem' }, loading: !!createPlanData.requesting || !!uploadData.requesting || !!validatePlanData.requesting }, { children: t('createPlan') })) }))] })) })), _jsx(UploadTemplateDialog, { open: isUpload, onClose: function () { return setIsUpload(!isUpload); }, handleUploadComplete: handleUploadComplete, uploadedFile: uploadedFile, setUploadedFile: setUploadedFile, setValue: setValue }), _jsx(UploadSuccessStatusDialog, { uploadDataId: uploadDataId })] }));
};
